/**
 * useUiMode.js — Détection du mode UI à afficher.
 *
 * Trois modes possibles depuis V1.1 (sprint vet context switcher) :
 *   - 'owner'        : shell propriétaire classique. Défaut sûr.
 *   - 'vet_pro'      : shell Mode Pro Vétérinaire (VetProRoot).
 *   - 'owner_as_vet' : shell propriétaire allégé pour véto qui veut
 *                       consulter ses Share entrants (OwnerAsVetShell).
 *
 * Conditions d'éligibilité au mode véto (vet_pro OU owner_as_vet) —
 * CUMULATIVES :
 *   1. L'utilisateur est authentifié (currentUser présent, auth résolue).
 *   2. currentUser.role === 'veterinaire'.
 *   3. process.env.REACT_APP_VET_PRO_FEATURE_FLAG === 'true' (strict).
 *
 * Si TOUTES remplies → consultation localStorage 'ekynair.vetUiContext' :
 *   - clé absente → 'vet_pro' (défaut sûr, préserve l'invariant V1.0).
 *   - JSON valide { schemaVersion: 1, context: 'owner_as_vet' } → 'owner_as_vet'.
 *   - Toute autre valeur (parse échec, schemaVersion ≠ 1, context inconnu)
 *     → 'vet_pro' (défaut sûr).
 *
 * Sinon (auth en cours, user absent, role non-véto, flag absent) →
 *   'owner' + PURGE défensive : si la clé localStorage est présente alors
 *   que l'user n'est PAS véto (cas du véto dégradé : un admin a rétrogradé
 *   le compte, ou flag retiré), on l'efface immédiatement via
 *   localStorage.removeItem direct. Empêche qu'un futur retour véto
 *   réactive automatiquement 'owner_as_vet' sans intervention explicite.
 *   PAS de purge pendant authPage='loading' / !currentUser : on ne mutile
 *   pas l'état tant que l'auth n'est pas résolue (anti-flash + anti
 *   destruction préférence valide).
 *
 * Hook **purement dérivé** : pas de useState, pas de useEffect. La
 * purge localStorage est un side effect intentionnel exécuté pendant
 * l'évaluation (garde-fou sécurité — D9bis V1.1, cf. précision Yannick
 * arbitrage 7 : purge via localStorage.removeItem direct, pas via le
 * setter useVetUiContext). Le setter du context (bascule explicite via
 * VetContextSwitcher) vit dans `useVetUiContext`, séparé.
 *
 * @returns {'owner' | 'vet_pro' | 'owner_as_vet'}
 *
 * Spec V1.0 §3.3, §6.1, D9 (DECISIONS 2026-05-12).
 * Spec V1.1 vet context switcher (DECISIONS 2026-05-17 — Dxx à venir).
 */
import { useAuth } from '../contexts/AuthContext';

const STORAGE_KEY = 'ekynair.vetUiContext';
const SCHEMA_VERSION = 1;

function safeRemove() {
  try {
    if (typeof localStorage !== 'undefined' && localStorage.getItem(STORAGE_KEY) !== null) {
      localStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    // quota / disabled / private mode — silencieux.
  }
}

function readContextPreference() {
  try {
    if (typeof localStorage === 'undefined') return null;
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.schemaVersion !== SCHEMA_VERSION) return null;
    if (parsed.context === 'owner_as_vet') return 'owner_as_vet';
    return null;
  } catch {
    return null;
  }
}

export function useUiMode() {
  const { currentUser, authPage } = useAuth();

  // Anti-flash : pendant loading ou avant auth, on ne touche à rien.
  if (authPage === 'loading' || !currentUser) return 'owner';

  const flagEnabled = process.env.REACT_APP_VET_PRO_FEATURE_FLAG === 'true';
  const isVetEligible = currentUser.role === 'veterinaire' && flagEnabled;

  if (!isVetEligible) {
    // Véto dégradé OU non-véto : purge défensive de la clé orpheline.
    safeRemove();
    return 'owner';
  }

  // Véto éligible : lecture du context préférence persistée.
  return readContextPreference() === 'owner_as_vet' ? 'owner_as_vet' : 'vet_pro';
}
